import { z } from "zod";
import { environmentsId } from "@papier/environments-schema";
import { projectsId } from "@papier/projects-schema";
import { TEMPLATE_TYPES } from "@papier/templates-contracts";
import { zodSlug } from "@papier/shared";
import { LOGS_STATUS_ENUM } from "./logs.enums";

export const DEFAULT_PAGE_INDEX = 0;
export const DEFAULT_PAGE_SIZE = 10;
export type SortLiteral = `${string}.${"asc" | "desc"}`;

// Schema for logs table filtering
const logsFilterSchema = z.object({
  environmentId: environmentsId.optional(),
  query: z.string().optional(),
  recipientType: z.array(z.enum(TEMPLATE_TYPES)).optional(),
  status: z.array(z.enum(LOGS_STATUS_ENUM)).optional(),
  sortBy: z
    .string()
    .regex(/^.+\.(asc|desc)$/)
    .optional()
    .transform((value) => value as SortLiteral | undefined),
  pageIndex: z.number().min(0).optional().default(DEFAULT_PAGE_INDEX),
  pageSize: z.number().min(1).optional().default(DEFAULT_PAGE_SIZE),
  startDate: z.string().optional(), // ISO date string
  endDate: z.string().optional(), // ISO date string
});

// Schema for logs aggregations filtering (analytics and charts)
export const logsAggregationsFilterSchema = z.object({
  environmentId: environmentsId.optional(),
  groupBy: z.enum(["date", "recipientType", "status"]).default("date"),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

// Schema for router search params
export const logsSearchSchema = logsFilterSchema
  .extend({
    env: zodSlug().optional(),
    pageIndex: z.number().min(0).optional(),
    pageSize: z.number().min(1).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  })
  .omit({ environmentId: true });

export type LogsFilterSchema = z.infer<typeof logsFilterSchema>;
export type LogsAggregationsFilterSchema = z.infer<typeof logsAggregationsFilterSchema>;

export default {
  getAll: z.object({
    projectId: projectsId.optional(),
    filters: logsFilterSchema,
  }),
  getAggregations: z.object({
    projectId: projectsId.optional(),
    filters: logsAggregationsFilterSchema,
  }),
};
